import React, { useState, useEffect } from 'react';
import { Users, Music2, Loader2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_URL, fetchWithAuth } from '../services/ApiService';
import SheetRoleAssignment from './SheetRoleAssignment';

/**
 * Component for managing all role assignments for an event's setlist
 */
const EventSetlistRoles = ({ event, setlist }) => {
  const navigate = useNavigate();
  const [sheets, setSheets] = useState([]);
  const [roleAssignments, setRoleAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedSheets, setExpandedSheets] = useState(new Set());
  
  // Fetch setlist sheets and existing role assignments
  useEffect(() => {
    if (!event || !setlist) return;
    
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get the setlist ID - handle both object and string formats
        const setlistId = typeof setlist === 'string' ? setlist : (setlist._id || setlist.id);
        
        if (!setlistId) {
          console.error('No valid setlist ID found:', setlist);
          setSheets([]);
          setLoading(false);
          return;
        }
        
        // First check if sheets are already populated in the setlist prop
        if (setlist.sheets && Array.isArray(setlist.sheets)) {
          console.log('Using sheets from setlist prop:', setlist.sheets);
          setSheets(setlist.sheets);
        } else {
          console.log('Fetching setlist details for ID:', setlistId);
          
          // Fetch setlist details with sheets
          const setlistResponse = await fetchWithAuth(`${API_URL}/setlists/${setlistId}`);
          console.log('Setlist response:', setlistResponse);
          
          if (setlistResponse.success && setlistResponse.data) {
            const setlistData = setlistResponse.data;
            console.log('Setlist sheets:', setlistData.sheets);
            setSheets(setlistData.sheets || []);
          } else {
            console.error('Failed to fetch setlist:', setlistResponse);
            setSheets([]);
          }
        }
        
        // Fetch existing role assignments for this event's setlist
        const rolesResponse = await fetchWithAuth(
          `${API_URL}/events/${event._id}/setlist/${setlistId}/roles`
        );
        
        console.log('Roles response:', rolesResponse);
        
        if (rolesResponse.success && rolesResponse.data) {
          // Convert array of role assignments to a map by sheet ID
          const rolesMap = {};
          rolesResponse.data.forEach(assignment => {
            rolesMap[assignment.sheet._id || assignment.sheet] = assignment;
          });
          setRoleAssignments(rolesMap);
        }
      } catch (error) {
        console.error('Error fetching setlist roles:', error);
        setSheets([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [event, setlist]);
  
  const handleRoleUpdate = (sheetId, updatedAssignment) => {
    setRoleAssignments(prev => ({
      ...prev,
      [sheetId]: updatedAssignment
    }));
  };
  
  const toggleSheetExpanded = (sheetId) => {
    setExpandedSheets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sheetId)) {
        newSet.delete(sheetId);
      } else {
        newSet.add(sheetId);
      }
      return newSet;
    });
  };
  
  // Get all unique performers across all sheets
  const getAllPerformers = () => {
    const performersSet = new Set();
    
    Object.values(roleAssignments).forEach(assignment => {
      if (assignment.roles) {
        // Add lead vocal
        if (assignment.roles.leadVocal) {
          performersSet.add(assignment.roles.leadVocal);
        }
        
        // Add backing vocals
        if (assignment.roles.backingVocals) {
          assignment.roles.backingVocals.forEach(v => performersSet.add(v));
        }
        
        // Add instrumentalists
        ['guitar', 'bass', 'drums', 'keys', 'saxophone', 'trumpet'].forEach(instrument => {
          if (assignment.roles[instrument]) {
            performersSet.add(assignment.roles[instrument]);
          }
        });
      }
    });
    
    // Also check sheet defaults
    sheets.forEach(sheet => {
      if (sheet.leadVocalist) {
        performersSet.add(sheet.leadVocalist);
      }
      if (sheet.arranger) {
        performersSet.add(sheet.arranger);
      }
    });
    
    return Array.from(performersSet).filter(p => p);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
        <span className="ml-2">Loading setlist...</span>
      </div>
    );
  }
  
  if (!sheets || sheets.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <Music2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No sheets in this setlist</p>
      </div>
    );
  }
  
  const performers = getAllPerformers();
  
  return (
    <div className="space-y-4">
      {/* Header with performer summary */}
      <div className="p-4 bg-muted/50 rounded-md">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium flex items-center gap-2">
            <Users className="w-4 h-4" />
            Role Assignments for {typeof setlist === 'object' ? setlist.name : 'Setlist'}
          </h3>
          <span className="text-sm text-muted-foreground">
            {sheets.length} song{sheets.length !== 1 ? 's' : ''}
          </span>
        </div>
        
        {performers.length > 0 && (
          <div className="mt-2">
            <p className="text-sm text-muted-foreground mb-1">Performers in this setlist:</p>
            <div className="flex flex-wrap gap-2">
              {performers.map(performer => (
                <span
                  key={performer}
                  className="text-xs px-2 py-1 bg-background rounded-md border"
                >
                  {performer}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Sheet list with role assignments */}
      <div className="space-y-2">
        {sheets.map((sheet, index) => {
          const isExpanded = expandedSheets.has(sheet._id);
          const roleAssignment = roleAssignments[sheet._id];
          const hasRoles = roleAssignment?.roles && Object.values(roleAssignment.roles).some(v => 
            Array.isArray(v) ? v.length > 0 : v
          );
          
          return (
            <div key={sheet._id} className="border rounded-md">
              <div
                className="p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => toggleSheetExpanded(sheet._id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      {index + 1}.
                    </span>
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        <span>{sheet.title}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/sheet/${sheet._id}`);
                          }}
                          className="text-primary hover:text-primary/80 transition-colors"
                          title="Open sheet"
                        >
                          <ExternalLink className="w-3 h-3" />
                        </button>
                        {sheet.artist && (
                          <span className="text-sm text-muted-foreground ml-1">
                            by {sheet.artist}
                          </span>
                        )}
                      </h4>
                      {(sheet.leadVocalist || sheet.arranger) && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {sheet.leadVocalist && (
                            <span>Lead: {sheet.leadVocalist}</span>
                          )}
                          {sheet.leadVocalist && sheet.arranger && ' • '}
                          {sheet.arranger && (
                            <span>Arr: {sheet.arranger}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {hasRoles && (
                      <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">
                        Roles assigned
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {isExpanded ? '▼' : '▶'}
                    </span>
                  </div>
                </div>
                
                {!isExpanded && hasRoles && (
                  <div className="mt-2">
                    <SheetRoleAssignment
                      sheet={sheet}
                      eventId={event._id}
                      setlistId={setlist._id || setlist}
                      existingRoles={roleAssignment}
                      onUpdate={(updated) => handleRoleUpdate(sheet._id, updated)}
                      isCompact={true}
                    />
                  </div>
                )}
              </div>
              
              {isExpanded && (
                <div className="border-t">
                  <SheetRoleAssignment
                    sheet={sheet}
                    eventId={event._id}
                    setlistId={setlist._id || setlist}
                    existingRoles={roleAssignment}
                    onUpdate={(updated) => handleRoleUpdate(sheet._id, updated)}
                    isCompact={false}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Summary */}
      <div className="mt-4 p-3 bg-muted/30 rounded-md text-sm text-muted-foreground">
        <p>
          💡 Tip: Click on a sheet to expand and assign roles. Lead vocalists shown are defaults from the sheet 
          unless overridden for this specific event.
        </p>
      </div>
    </div>
  );
};

export default EventSetlistRoles;